import type { Auth0ContextInterface, User } from '@auth0/auth0-react';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import { getEnv } from '@risksmart-app/components/src/utils/environment';
import type { AppRouter } from '@risksmart-app/trpc/src/routers/router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createTRPCClient, httpBatchLink } from '@trpc/client';
import type { Context, FC, ReactNode } from 'react';
import { useMemo } from 'react';
import superjson from 'superjson';

import { TRPCProvider as TanstackTRPCProvider } from '../utils/trpc';

interface Props {
  authContext?: Context<Auth0ContextInterface<User>>;
  children: ReactNode;
}

const makeQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Attempt to recreate the 'no-cache' fetch policy from Apollo Client, exceptions on a case by case basis
        gcTime: 0, // Don't keep data in cache after component unmounts
        staleTime: 0, // Always consider data stale
        refetchOnMount: true, // Always refetch when component mounts
        refetchOnWindowFocus: false, // Don't refetch on window focus
      },
    },
  });
};
let browserQueryClient: QueryClient | undefined = undefined;
const getQueryClient = () => {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return makeQueryClient();
  } else {
    // Browser: make a new query client if we don't already have one
    if (!browserQueryClient) {
      browserQueryClient = makeQueryClient();
    }

    return browserQueryClient;
  }
};

export const TrpcProvider: FC<Props> = ({ children, authContext }) => {
  const { getAccessTokenSilently, isLoading, isAuthenticated } =
    useRisksmartUser(authContext);

  const queryClient = useMemo(() => getQueryClient(), []);

  const trpcClient = useMemo(() => {
    return createTRPCClient<AppRouter>({
      links: [
        httpBatchLink({
          url: `${getEnv('REACT_APP_TRPC_API_URL')}/trpc`,
          maxItems: getEnv('REACT_APP_ENVIRONMENT') === 'dev-local' ? 1 : 10,
          transformer: superjson,
          async headers() {
            if (!isLoading && isAuthenticated) {
              try {
                const token = await getAccessTokenSilently();
                console.log('tRPC: Fetched token successfully.');

                return {
                  Authorization: `Bearer ${token}`,
                };
              } catch (error) {
                console.error(
                  'tRPC: Failed to get access token silently:',
                  error
                );

                return {};
              }
            } else if (isLoading) {
              // If Auth0 is still loading, don't try to fetch token yet.
              console.log('tRPC: Auth0 loading, request sent without token.');

              return {};
            } else {
              // If Auth0 is done loading but user is not authenticated
              console.log(
                'tRPC: User not authenticated, request sent without token.'
              );

              return {};
            }
          },
        }),
      ],
    });
  }, [getAccessTokenSilently, isLoading, isAuthenticated]);

  return (
    <QueryClientProvider client={queryClient}>
      <TanstackTRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {children}
      </TanstackTRPCProvider>
    </QueryClientProvider>
  );
};
